/**
 * Email service using Resend
 * Handles sending all email types with customizable templates
 */

import { Resend } from 'resend';
import { renderEmailTemplate, getEmailBrandingFromAccount as getExtendedBranding } from './email-renderer.js';
import { logger } from './logger.js';

export interface EmailConfig {
  apiKey: string;
  fromEmail: string;
  fromName?: string;
  baseUrl?: string; // Base URL for generating task links
  organizationName?: string; // Organization name to prefix email subjects
}

/**
 * Email branding configuration for customizing email templates per organization
 */
export interface EmailBranding {
  replyToEmail?: string; // Email address for recipients to reply to
  logoUrl?: string; // URL to organization logo (should be publicly accessible)
  primaryColor?: string; // Primary brand color (e.g., "#667eea")
  secondaryColor?: string; // Secondary brand color (e.g., "#764ba2")
  footerText?: string; // Custom footer text (defaults to "This is an automated reminder from LittleBooker.")

  // Custom copy for each email template (optional - uses defaults if not provided)
  templates?: {
    taskReminder?: {
      greeting?: string; // Custom greeting (defaults to "Hi {name},")
      intro?: string; // Custom intro text
      footer?: string; // Custom footer text for this template
    };
    profileCompletion?: {
      greeting?: string;
      intro?: string;
      footer?: string;
    };
    happeningCompletion?: {
      greeting?: string;
      intro?: string;
      footer?: string;
    };
    happeningStubLink?: {
      greeting?: string;
      intro?: string;
      footer?: string;
    };
    happeningReminder?: {
      greeting?: string;
      intro?: string;
      footer?: string;
    };
  };
}

let emailConfig: EmailConfig | null = null;
let resendClient: Resend | null = null;

/**
 * Initialize the email service with Resend configuration
 */
export function initEmailService(config: EmailConfig): void {
  emailConfig = config;
  resendClient = new Resend(config.apiKey);
}

/**
 * Update the organization name in the email configuration
 */
export function updateEmailOrganizationName(organizationName: string): void {
  if (emailConfig) {
    emailConfig.organizationName = organizationName;
  }
}

/**
 * Get the configured base URL for generating links
 */
function getBaseUrl(): string {
  return emailConfig?.baseUrl || process.env.ADMIN_BASE_URL || 'http://localhost:5173';
}

/**
 * Format a date string in a specific timezone using Intl.DateTimeFormat.
 * When the format includes a time component (hour), appends the timezone
 * abbreviation in brackets, e.g., "Friday, February 15, 2025, 7:00 PM [CET]".
 * Falls back to UTC if no timezone is provided.
 */
export function formatDateInTimezone(
  dateStr: string,
  timezone: string | undefined,
  options: Intl.DateTimeFormatOptions
): string {
  const date = new Date(dateStr);
  const tz = timezone || 'UTC';
  const formatted = new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(date);

  // If format includes time, append timezone abbreviation in brackets
  if (options.hour !== undefined) {
    const tzAbbr = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value || tz;
    return `${formatted} [${tzAbbr}]`;
  }

  return formatted;
}

/**
 * Generate a link to a task in the admin interface
 */
export function generateTaskLink(taskId: string): string {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/tasks?openEntityId=${taskId}&entityType=task`;
}

/**
 * Send a task reminder email
 */
export async function sendTaskReminder(params: {
  toEmail: string;
  toName: string;
  taskTitle: string;
  taskDescription: string | null;
  taskDueDate: string;
  taskId: string;
  taskPriority: string;
  taskType: string;
  organizationName?: string; // Optional override for organization name
  branding?: EmailBranding; // Optional email branding customization
  relatedHappening?: {
    title: string;
    startTime: string;
    type?: string;
    place?: { name: string; address?: string } | null;
  } | null;
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const taskLink = generateTaskLink(params.taskId);
  const formattedDueDate = formatDateInTimezone(params.taskDueDate, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';
  const branding = params.branding;
  const colors = getBrandingColors(branding);

  // Format related happening data
  const happeningStartTime = params.relatedHappening ? formatDateInTimezone(params.relatedHappening.startTime, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }) : '';

  const happeningSectionHtml = params.relatedHappening ? `
    <div style="background: #f0f9ff; border-left: 4px solid ${colors.secondary}; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <h3 style="margin: 0 0 10px 0; font-size: 16px; color: #111827;">Related Happening</h3>
      <p style="margin: 0 0 8px 0; font-size: 14px; color: #6b7280;">
        <strong style="color: #111827;">${params.relatedHappening.title}</strong>
        ${params.relatedHappening.type ? `<span style="margin-left: 10px; padding: 2px 8px; background: ${colors.primary}; color: white; border-radius: 3px; font-size: 12px; text-transform: capitalize;">${params.relatedHappening.type}</span>` : ''}
      </p>
      <p style="margin: 0; font-size: 13px; color: #6b7280;">
        <strong>Starts:</strong> ${happeningStartTime}
      </p>
      ${params.relatedHappening.place ? `
      <p style="margin: 8px 0 0 0; font-size: 13px; color: #6b7280;">
        <strong>Location:</strong> ${params.relatedHappening.place.name}${params.relatedHappening.place.address ? ` — ${params.relatedHappening.place.address}` : ''}
      </p>
      ` : ''}
    </div>
  ` : '';

  const happeningSectionText = params.relatedHappening ? `
Related Happening:
${params.relatedHappening.title}${params.relatedHappening.type ? ` (${params.relatedHappening.type})` : ''}
Starts: ${happeningStartTime}
${params.relatedHappening.place ? `Location: ${params.relatedHappening.place.name}${params.relatedHappening.place.address ? `\n${params.relatedHappening.place.address}` : ''}` : ''}
  `.trim() : '';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    taskTitle: params.taskTitle,
    taskDescription: params.taskDescription || '',
    taskDescriptionHtml: params.taskDescription ? `<p style="margin: 10px 0; color: #6b7280; font-size: 14px;">${params.taskDescription}</p>` : '',
    taskDueDate: formattedDueDate,
    taskType: params.taskType,
    taskPriority: params.taskPriority,
    taskPriorityColor: getPriorityColor(params.taskPriority),
    taskLink,
    organizationName: orgName,
    happeningSectionHtml,
    happeningSectionText,
    relatedHappening: params.relatedHappening ? {
      ...params.relatedHappening,
      startTime: happeningStartTime,
    } : undefined,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.taskReminder;

  // Render email using template engine
  const rendered = renderEmailTemplate('taskReminder', variables, customTemplate, branding);

  if (!resendClient) {
    throw new Error('Resend client not initialized. Call initEmailService() first.');
  }

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'taskReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      taskId: params.taskId,
      taskTitle: params.taskTitle,
      taskPriority: params.taskPriority,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'taskReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      taskId: params.taskId,
      taskTitle: params.taskTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Get color for priority badge
 */
function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'urgent':
      return '#dc2626';
    case 'high':
      return '#ea580c';
    case 'medium':
      return '#ca8a04';
    case 'low':
      return '#6b7280';
    default:
      return '#6b7280';
  }
}

/**
 * Get branding colors, defaulting to LittleBooker brand colors
 */
function getBrandingColors(branding?: EmailBranding): { primary: string; secondary: string } {
  return {
    primary: branding?.primaryColor || '#667eea',
    secondary: branding?.secondaryColor || '#764ba2',
  };
}

/**
 * Generate logo HTML if branding includes a logo
 */
function getLogoHtml(branding?: EmailBranding): string {
  if (!branding?.logoUrl) return '';
  return `
    <div style="margin-bottom: 20px;">
      <img src="${branding.logoUrl}" alt="Logo" style="max-width: 200px; max-height: 60px; height: auto;" />
    </div>
  `;
}

/**
 * Get custom template text or fall back to default
 */
function getTemplateText(
  branding: EmailBranding | undefined,
  templateType: keyof NonNullable<EmailBranding['templates']>,
  field: 'greeting' | 'intro' | 'footer',
  defaultValue: string
): string {
  return branding?.templates?.[templateType]?.[field] || defaultValue;
}

/**
 * Send a profile completion reminder email
 */
export async function sendProfileCompletionReminder(params: {
  toEmail: string;
  toName: string;
  personName: string;
  missingFields: string[];
  inviteLink: string;
  completionDeadline?: string; // ISO date string for profile completion deadline
  organizationName?: string; // Optional override for organization name
  branding?: EmailBranding; // Optional email branding customization
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedDeadline = params.completionDeadline
    ? formatDateInTimezone(params.completionDeadline, params.timezone, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    personName: params.personName,
    missingFieldsList: params.missingFields.map(field => `• ${field}`).join('<br>'),
    missingFieldsText: params.missingFields.map(field => `• ${field}`).join('\n'),
    inviteLink: params.inviteLink,
    completionDeadline: formattedDeadline,
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.profileCompletion;

  // Render email using template engine
  const rendered = renderEmailTemplate('profileCompletion', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'profileCompletionReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      personName: params.personName,
      missingFieldCount: params.missingFields.length,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'profileCompletionReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      personName: params.personName,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a happening completion reminder email
 */
export async function sendHappeningCompletionReminder(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  happeningStartTime: string; // ISO date string for when the happening starts
  missingFields: string[];
  stubLink: string;
  organizationName?: string; // Optional override for organization name
  branding?: EmailBranding; // Optional email branding customization
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedStartTime = formatDateInTimezone(params.happeningStartTime, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    happeningStartTime: formattedStartTime,
    missingFieldsList: params.missingFields.map(field => `• ${field}`).join('<br>'),
    missingFieldsText: params.missingFields.map(field => `• ${field}`).join('\n'),
    stubLink: params.stubLink,
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.happeningCompletion;

  // Render email using template engine
  const rendered = renderEmailTemplate('happeningCompletion', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'happeningCompletionReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      missingFieldCount: params.missingFields.length,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'happeningCompletionReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send an immediate email with the stub link (when stub link is generated)
 */
export async function sendHappeningStubLinkEmail(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  stubLink: string;
  completionDeadline: string; // ISO date string for when show details must be completed
  happeningStartTime?: string; // ISO date string for when the happening starts
  organizationName?: string; // Optional override for organization name
  branding?: EmailBranding; // Optional email branding customization
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedDeadline = formatDateInTimezone(params.completionDeadline, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Format happening start time if provided
  let formattedStartTime: string | undefined;
  if (params.happeningStartTime) {
    formattedStartTime = formatDateInTimezone(params.happeningStartTime, params.timezone, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).replace(',', ' at');
    // Format: "Friday, Jan 26 at 10:00 PM [CET]"
  }

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    stubLink: params.stubLink,
    completionDeadline: formattedDeadline,
    happeningStartTime: formattedStartTime || '',
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.happeningStubLink;

  // Render email using template engine
  const rendered = renderEmailTemplate('happeningStubLink', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'happeningStubLink',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'happeningStubLink',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a happening reminder email to an artist
 */
export async function sendHappeningReminder(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  happeningStartTime: string;
  happeningPlace?: {
    name: string;
    address?: string;
  } | null;
  arrivalInfo?: string; // Optional arrival time or instructions
  stubLink?: string; // Optional link to edit show details
  organizationName?: string; // Optional override for organization name
  branding?: EmailBranding; // Optional email branding customization
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedStartTime = formatDateInTimezone(params.happeningStartTime, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  const locationSectionHtml = params.happeningPlace ? `
    <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
      <div style="font-size: 14px;">
        <strong style="color: #6b7280;">Location:</strong>
        <div style="margin-top: 5px; color: #111827;">
          ${params.happeningPlace.name}
          ${params.happeningPlace.address ? `<br>${params.happeningPlace.address}` : ''}
        </div>
      </div>
    </div>
  ` : '';

  const locationSectionText = params.happeningPlace ?
    `Location: ${params.happeningPlace.name}${params.happeningPlace.address ? `\n${params.happeningPlace.address}` : ''}` : '';

  const arrivalSectionHtml = params.arrivalInfo ? `
    <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
      <div style="font-size: 14px;">
        <strong style="color: #6b7280;">Arrival:</strong>
        <div style="margin-top: 5px; color: #111827;">
          ${params.arrivalInfo}
        </div>
      </div>
    </div>
  ` : '';

  const arrivalSectionText = params.arrivalInfo ? `Arrival: ${params.arrivalInfo}` : '';

  // Build edit link sections if stubLink is provided
  const colors = getBrandingColors(params.branding);
  const editLinkHtml = params.stubLink ? `
    <div style="text-align: center; margin: 25px 0;">
      <a href="${params.stubLink}"
         style="display: inline-block; background: ${colors.primary}; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 16px;">
        Edit Show Details →
      </a>
    </div>
  ` : '';

  const editLinkText = params.stubLink ? `Edit Show Details: ${params.stubLink}` : '';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    happeningStartTime: formattedStartTime,
    organizationName: orgName,
    locationSectionHtml,
    locationSectionText,
    arrivalSectionHtml,
    arrivalSectionText,
    arrivalInfo: params.arrivalInfo || '',
    happeningPlace: params.happeningPlace,
    stubLink: params.stubLink || '',
    editLinkHtml,
    editLinkText,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.happeningReminder;

  // Render email using template engine
  const rendered = renderEmailTemplate('happeningReminder', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'happeningReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'happeningReminder',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a completion notification email to staff
 */
export async function sendCompletionNotificationEmail(params: {
  toEmail: string;
  toName: string;
  entityType: 'person' | 'happening';
  entityName: string;
  completedByName: string;
  completedAt: string; // ISO date string
  viewLink: string;
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedCompletedAt = formatDateInTimezone(params.completedAt, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';
  const entityTypeLabel = params.entityType === 'person' ? 'Profile' : 'Show Details';

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    entityType: params.entityType,
    entityTypeLabel,
    entityName: params.entityName,
    completedByName: params.completedByName,
    completedAt: formattedCompletedAt,
    viewLink: params.viewLink,
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.completionNotification;

  // Render email using template engine
  const rendered = renderEmailTemplate('completionNotification', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'completionNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      entityType: params.entityType,
      entityName: params.entityName,
      completedByName: params.completedByName,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'completionNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      entityType: params.entityType,
      entityName: params.entityName,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a technical update notification email to an engineer
 * Triggered when an artist updates equipment requests or technical notes
 */
export async function sendTechnicalUpdateNotificationEmail(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  updatedByName: string;
  updatedAt: string; // ISO date string
  changeDescription: string;
  viewLink: string;
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string;
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedUpdatedAt = formatDateInTimezone(params.updatedAt, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    updatedByName: params.updatedByName,
    updatedAt: formattedUpdatedAt,
    changeDescription: params.changeDescription,
    viewLink: params.viewLink,
    organizationName: orgName,
  };

  const customTemplate = (params.branding as any)?.emailTemplates?.technicalUpdateNotification;
  const rendered = renderEmailTemplate('technicalUpdateNotification', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'technicalUpdateNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      updatedByName: params.updatedByName,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'technicalUpdateNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a combined artist and show invite email
 * Combines profile completion and show details into one cohesive email
 */
export async function sendCombinedArtistShowInvite(params: {
  toEmail: string;
  toName: string;
  artistName: string;
  missingProfileFields: string; // comma-separated
  profileLink: string;
  happeningTitle: string;
  happeningStartTime: string; // formatted string
  happeningPlace: string;
  happeningDuration: number;
  showLink: string;
  combinedLink?: string; // single URL to combined profile+show page
  completionDeadline?: string; // ISO date string for when details must be completed
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';

  // Format completion deadline if provided
  let formattedDeadline: string | undefined;
  if (params.completionDeadline) {
    formattedDeadline = formatDateInTimezone(params.completionDeadline, params.timezone, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Prepare variables for template rendering
  const variables = {
    artistName: params.artistName,
    missingProfileFields: params.missingProfileFields,
    profileLink: params.profileLink,
    happeningTitle: params.happeningTitle,
    happeningStartTime: params.happeningStartTime,
    happeningPlace: params.happeningPlace,
    showLink: params.showLink,
    combinedLink: params.combinedLink || '',
    happeningDuration: params.happeningDuration,
    completionDeadline: formattedDeadline || '',
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  // params.branding may already be extended with emailTemplates from the scheduler
  const customTemplate = (params.branding as any)?.emailTemplates?.artistAndShowInvite;

  // Render email using template engine
  const rendered = renderEmailTemplate('artistAndShowInvite', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'combinedArtistShowInvite',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      artistName: params.artistName,
      happeningTitle: params.happeningTitle,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'combinedArtistShowInvite',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      artistName: params.artistName,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a residency creation email to an artist
 * Lists all shows created for the residency with edit links
 */
export async function sendResidencyCreationEmail(params: {
  toEmail: string;
  toName: string;
  seriesName: string;
  seriesDescription?: string | null;
  episodeCount: number;
  frequency: string;
  shows: Array<{
    title: string;
    startTime: string;
    stubLink: string;
    episodeNumber?: number;
  }>;
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string; // IANA timezone identifier for date formatting
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';
  const colors = getBrandingColors(params.branding);

  // Build show list HTML
  const showListHtml = `
    <div style="border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden;">
      ${params.shows.map((show, index) => {
        const formattedDate = formatDateInTimezone(show.startTime, params.timezone, {
          weekday: 'long',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
        const episodeLabel = show.episodeNumber ? `Ep. ${show.episodeNumber} - ` : '';
        const isLast = index === params.shows.length - 1;
        return `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; ${!isLast ? 'border-bottom: 1px solid #e5e7eb;' : ''} ${index % 2 === 0 ? 'background: #f9fafb;' : 'background: white;'}">
            <span style="font-weight: 500; color: #111827;">${episodeLabel}${formattedDate}</span>
            <a href="${show.stubLink}" style="color: ${colors.primary}; text-decoration: none; font-size: 14px;">Edit Details →</a>
          </div>
        `;
      }).join('')}
    </div>
  `;

  // Build show list text
  const showListText = params.shows.map((show, index) => {
    const formattedDate = formatDateInTimezone(show.startTime, params.timezone, {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    const episodeLabel = show.episodeNumber ? `Ep. ${show.episodeNumber} - ` : '';
    return `${index + 1}. ${episodeLabel}${formattedDate}\n   Edit: ${show.stubLink}`;
  }).join('\n\n');

  // Prepare variables for template rendering
  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    seriesName: params.seriesName,
    seriesDescription: params.seriesDescription || '',
    episodeCount: params.episodeCount,
    frequency: params.frequency,
    showListHtml,
    showListText,
    organizationName: orgName,
  };

  // Get custom template from branding (if exists)
  const customTemplate = (params.branding as any)?.emailTemplates?.residencyCreation;

  // Render email using template engine
  const rendered = renderEmailTemplate('residencyCreation', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'residencyCreation',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      seriesName: params.seriesName,
      episodeCount: params.episodeCount,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'residencyCreation',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      seriesName: params.seriesName,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Send a post-show follow-up email to an artist
 */
export async function sendPostShowEmail(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  happeningStartTime: string; // ISO date string
  tracklistLink: string;
  feedbackLink?: string; // Optional external feedback form URL
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string;
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  const formattedStartTime = formatDateInTimezone(params.happeningStartTime, params.timezone, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';
  const colors = getBrandingColors(params.branding);

  // Build feedback section conditionally
  let feedbackSectionHtml = '';
  let feedbackSectionText = '';
  if (params.feedbackLink) {
    feedbackSectionHtml = `
      <div style="text-align: center; margin: 15px 0;">
        <a href="${params.feedbackLink}"
           style="display: inline-block; background: ${colors.primary}; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 15px;">
          Share Your Feedback →
        </a>
      </div>
    `;
    feedbackSectionText = `SHARE YOUR FEEDBACK:\n${params.feedbackLink}`;
  }

  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    happeningStartTime: formattedStartTime,
    tracklistLink: params.tracklistLink,
    feedbackSectionHtml,
    feedbackSectionText,
    organizationName: orgName,
  };

  const customTemplate = (params.branding as any)?.emailTemplates?.postShow;
  const rendered = renderEmailTemplate('postShow', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'postShow',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'postShow',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

export interface ArchiveLinkForEmail {
  label: string; // Display label, e.g. "SoundCloud" or the custom name
  url: string;
}

/**
 * Send an archive notification email to an artist when their show gains an archive/embed link.
 */
export async function sendArchiveNotificationEmail(params: {
  toEmail: string;
  toName: string;
  happeningTitle: string;
  happeningStartTime: string; // ISO date string
  showPageUrl?: string; // Public site URL for the show. Omit/empty if websiteBaseUrl not configured.
  /** The recipient's own set within a takeover's recording, when a running order is set. */
  yourSet?: { startsAt: string; listenUrl?: string };
  archiveLinks: ArchiveLinkForEmail[];
  organizationName?: string;
  branding?: EmailBranding;
  timezone?: string;
}): Promise<void> {
  if (!emailConfig || !resendClient) {
    throw new Error('Email service not initialized. Call initEmailService() first.');
  }

  if (params.archiveLinks.length === 0) {
    throw new Error('sendArchiveNotificationEmail requires at least one archive link');
  }

  const formattedStartTime = params.happeningStartTime
    ? formatDateInTimezone(params.happeningStartTime, params.timezone, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  const orgName = params.organizationName || emailConfig.organizationName || 'LittleBooker';
  const colors = getBrandingColors(params.branding);

  let showPageSectionHtml = '';
  let showPageSectionText = '';
  if (params.showPageUrl) {
    showPageSectionHtml = `
      <div style="text-align: center; margin: 15px 0;">
        <a href="${params.showPageUrl}"
           style="display: inline-block; background: ${colors.primary}; color: white; text-decoration: none; padding: 12px 30px; border-radius: 6px; font-weight: 600; font-size: 15px;">
          View show page →
        </a>
      </div>
    `;
    showPageSectionText = `Show page: ${params.showPageUrl}\n`;
  }

  // Rides inside the show-page section so accounts with a custom archive template
  // (which already place {{showPageSectionHtml}}) get it without editing the template.
  if (params.yourSet) {
    const listen = params.yourSet.listenUrl
      ? ` <a href="${params.yourSet.listenUrl}" style="color: ${colors.primary};">Listen from your set →</a>`
      : '';
    showPageSectionHtml =
      `<p style="font-size: 15px; margin: 15px 0; color: #111827; text-align: center;">` +
      `Your set starts at <strong>${params.yourSet.startsAt}</strong> in the recording.${listen}</p>` +
      showPageSectionHtml;
    showPageSectionText =
      `Your set starts at ${params.yourSet.startsAt} in the recording.\n` +
      (params.yourSet.listenUrl ? `Listen from your set: ${params.yourSet.listenUrl}\n` : '') +
      showPageSectionText;
  }

  const archiveLinksHtml = `<ul style="margin: 0; padding-left: 20px;">${params.archiveLinks
    .map(
      (l) =>
        `<li style="margin: 4px 0;"><a href="${l.url}" style="color: ${colors.primary};">${l.label}</a></li>`,
    )
    .join('')}</ul>`;
  const archiveLinksText = params.archiveLinks.map((l) => `- ${l.label}: ${l.url}`).join('\n');

  const archiveLinksHeading = params.showPageUrl
    ? 'Besides the website, the show is also archived on:'
    : 'The show is archived on:';

  const variables = {
    toEmail: params.toEmail,
    toName: params.toName,
    happeningTitle: params.happeningTitle,
    happeningStartTime: formattedStartTime,
    showPageSectionHtml,
    showPageSectionText,
    archiveLinksHeading,
    archiveLinksHtml,
    archiveLinksText,
    organizationName: orgName,
  };

  const customTemplate = (params.branding as any)?.emailTemplates?.archiveNotification;
  const rendered = renderEmailTemplate('archiveNotification', variables, customTemplate, params.branding);

  const senderName = orgName || emailConfig.fromName || 'LittleBooker';

  try {
    const result = await resendClient.emails.send({
      from: `${senderName} <${emailConfig.fromEmail}>`,
      to: params.toEmail,
      subject: rendered.subject,
      text: rendered.text,
      html: rendered.html,
      replyTo: params.branding?.replyToEmail,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Failed to send email');
    }

    logger.info('email_sent', {
      emailType: 'archiveNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      resendId: result.data?.id,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      archiveLinkCount: params.archiveLinks.length,
    });
  } catch (error: any) {
    logger.error('email_send_failed', {
      emailType: 'archiveNotification',
      recipientEmail: params.toEmail,
      recipientName: params.toName,
      organizationName: orgName,
      subject: rendered.subject,
      happeningTitle: params.happeningTitle,
      error: error?.message || 'Unknown error',
      resendError: error?.response?.body,
    });

    throw error;
  }
}

/**
 * Check if email service is configured
 */
export function isEmailServiceConfigured(): boolean {
  return emailConfig !== null;
}

/**
 * Extract email branding from account settings
 * Looks for account.settings.emailBranding and returns it as EmailBranding
 */
export function getEmailBrandingFromAccount(account: { settings?: Record<string, unknown> | string }): EmailBranding | undefined {
  // Handle case where settings might be a JSON string (shouldn't happen with PostgREST, but be safe)
  let settings: Record<string, unknown> | undefined;
  if (typeof account.settings === 'string') {
    try {
      settings = JSON.parse(account.settings) as Record<string, unknown>;
    } catch (e) {
      logger.warn('email_branding_parse_failed', { error: String(e) });
      return undefined;
    }
  } else if (account.settings && typeof account.settings === 'object') {
    settings = account.settings;
  } else {
    return undefined;
  }

  const emailBranding = settings.emailBranding;
  if (!emailBranding || typeof emailBranding !== 'object') {
    return undefined;
  }

  return emailBranding as EmailBranding;
}
